import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { IoSearchOutline, IoArrowForward, IoChevronDown, IoArrowBack } from "react-icons/io5";
import TermsAndCondition from "./TermsAndCondition";
import PrivacyPolicy from "./PrivacyPolicy";
import CommunityGuidelines from "./CommunityGuidelines";

const DOCUMENT_COMPONENTS = {
    terms: TermsAndCondition,
    privacy: PrivacyPolicy,
    community: CommunityGuidelines,
};

const CATEGORIES = [
    {
        key: "terms",
        label: "Terms and Conditions",
        number: 1,
        sections: [
            { id: "1.0", title: "Terms and Conditions", excerpt: "The rules and agreements every member accepts when using Chaa Chingg's website and app." },
            { id: "1.1", title: "Acceptable Use", excerpt: "What you can and can't do on the platform, including account eligibility and prohibited behavior." },
            { id: "1.2", title: "Account Termination", excerpt: "When and how we may suspend or terminate an account for violating our agreement." },
        ],
    },
    {
        key: "privacy",
        label: "Privacy Policy",
        number: 2,
        sections: [
            { id: "2.0", title: "Privacy Policy", excerpt: "How Chaa Chingg collects, uses, shares, and protects your personal information." },
            { id: "2.1", title: "Data Collection", excerpt: "The types of information we gather from you and from your use of the platform." },
            { id: "2.2", title: "Your Privacy Controls", excerpt: "Manage what you share, who can see it, and how your data is used." },
        ],
    },
    {
        key: "community",
        label: "Community Guidelines",
        number: 3,
        sections: [
            { id: "3.0", title: "Community Guidelines", excerpt: "Our standards for respectful, safe behavior across posts, comments, and messages. These guidelines apply to everyone on Chaa Chingg and explain what's expected of you as a member of the community." },
            { id: "3.1", title: "Prohibited Content", excerpt: "Content that isn't allowed on Chaa Chingg, including harassment, hate speech, and violence." },
            { id: "3.2", title: "Respectful Interactions", excerpt: "Guidelines for how members should treat each other while using the platform." },
        ],
    },
    {
        key: "faq",
        label: "FAQ",
        number: 4,
        sections: [
            {
                id: "5.1",
                title: "Who can see my location on Chaa Chingg?",
                excerpt: "Chaa Chingg is hyperlocal, but we never show your precise location — adults are placed at the city level and teens at the county level.",
                answer: "Chaa Chingg is hyperlocal, so your posts are surfaced to people in your area — but we never show your precise location. Adults are placed at the city level, and members aged 13–17 are placed at the county level only, which is a much wider area. Nobody sees an address, a pin on a map, or a live location. You can also post to the national Homepage Feed instead of your Local City Feed if you'd rather not be tied to a place at all.",
            },
            {
                id: "5.2",
                title: "How do I report a post, comment, message, or account?",
                excerpt: "Reporting takes two taps — the first depends on whether you're reporting a post, comment, message, or account.",
                answer: "Reporting takes two taps, though the first one depends on what you're reporting:\n• Post — tap the Share icon, then tap Report.\n• Comment — tap the three-dot icon, then tap Report.\n• Chat message — press and hold the message, then tap Report.\n• Account — open the profile, tap the three-dot icon in the top right, then tap Report.\n\nFrom there, choose the reason that best fits and add any detail you think will help — context makes reviews faster and more accurate. Reports are confidential; the person you report is never told who filed it.",
            },
            {
                id: "5.3",
                title: "What happens after I submit a report?",
                excerpt: "Reports go to our in-house moderation team, with urgent safety issues prioritized and handled sooner.",
                answer: "Every report goes to our in-house moderation team. Most reports are reviewed within one to two days, and urgent ones — anything involving safety, threats, or harm — are prioritized and handled sooner. Content that violates our Community Guidelines is removed, and the account may be warned, restricted, or removed depending on severity and history. You'll get a notification once a decision is made on your report.",
            },
            {
                id: "5.4",
                title: "What happens when I block someone?",
                excerpt: "Blocking cuts the connection in both directions, and the blocked member is never notified.",
                answer: "Blocking cuts the connection in both directions. A blocked member can't view your profile, see your posts, message you, or interact with your content in any way — and you won't see theirs either. They aren't notified that you've blocked them. You can block someone from their profile or directly from a post, comment, or chat message, and you can unblock them at any time from Settings → Your Activity → Blocked Accounts.",
            },
            {
                id: "5.5",
                title: "What are the rules for members under 18?",
                excerpt: "Teens aged 13–17 get stricter defaults automatically, plus parental controls available in Settings.",
                answer: "Chaa Chingg is for members 13 and up. We ask for your date of birth at sign-up, and anyone under 13 can't create an account. Members aged 13–17 get stricter defaults automatically: location is limited to the county level rather than the city, and anyone a teen doesn't follow can send them only one direct message — if the teen doesn't reply, that person can't message again. Parental controls are available in Settings, including daily time limits and a neighborhood toggle that turns off location-based feeds. Teen safety is something we're actively and continuously improving, so these protections will keep expanding as the platform grows.",
            },
            {
                id: "5.6",
                title: "My account was suspended or my post was removed. Can I appeal?",
                excerpt: "Yes — appeal any time through the Report Center, and a different moderator will review it.",
                answer: "Yes, and there's no deadline — you can appeal at any time. Submit your appeal through the Report Center in Settings. Appeals are reviewed by a different moderator than the one who made the original decision, and if we got it wrong, we restore the content or account and clear the strike.",
            },
            {
                id: "5.7",
                title: "I can't get into my account — what do I do?",
                excerpt: "Use the \"Forgot password\" link, or email support@chaachingg.com if you no longer have access to your email.",
                answer: "Start with the \"Forgot password\" link on the login screen and follow the reset sent to your registered email. If you no longer have access to that email, or you think someone else has taken over your account, email us at support@chaachingg.com right away. Don't create a new account in the meantime — it makes recovery harder. Once you're back in, change your password.",
            },
        ],
    },
];

const QUICK_LINKS = ["terms", "privacy", "community", "faq"];

const SUPPORT_EMAIL = "support@chaachingg.com";

function renderWithEmailLink(text) {
    if (!text || !text.includes(SUPPORT_EMAIL)) return text;
    const [before, after] = text.split(SUPPORT_EMAIL);
    return (
        <>
            {before}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold text-[#2E9DDA] hover:underline">
                {SUPPORT_EMAIL}
            </a>
            {after}
        </>
    );
}

function FilterPanelContent({ searchQuery, onSearchChange, activeCategory, onCategoryClick }) {
    return (
        <div className="px-5 pb-5 pt-1 md:pt-5">
            <div className="relative">
                <input
                    type="text"
                    value={searchQuery}
                    onChange={onSearchChange}
                    placeholder="Search"
                    className="h-11 w-full rounded-full border border-[#D5E7F2] bg-white pl-4 pr-10 text-[13px] text-black outline-none focus:border-[#5BA9DC]"
                />
                <IoSearchOutline className="absolute right-3 top-1/2 -translate-y-1/2 text-[16px] text-[#7A8A95]" />
            </div>

            <nav className="mt-4 flex flex-col gap-3">
                {CATEGORIES.map((category) => (
                    <button
                        key={category.key}
                        type="button"
                        onClick={() => onCategoryClick(category.key)}
                        className={`h-11 w-full rounded-2xl text-[14px] font-semibold transition-colors ${activeCategory === category.key
                            ? "bg-[#000000] text-white"
                            : "bg-[#BEE6FF] text-black hover:bg-[#A8DAFA]"
                            }`}
                    >
                        {category.label}
                    </button>
                ))}
            </nav>
        </div>
    );
}

export default function TrustAndSafety() {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState("");
    const [activeCategory, setActiveCategory] = useState(null);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const [expandedId, setExpandedId] = useState(null);

    const allSections = useMemo(
        () => CATEGORIES.flatMap((category) => category.sections.map((section) => ({ ...section, category }))),
        []
    );

    const trimmedQuery = searchQuery.trim().toLowerCase();

    const results = useMemo(() => {
        if (activeCategory) {
            return allSections.filter((section) => section.category.key === activeCategory);
        }
        if (!trimmedQuery) return null;
        return allSections.filter(
            (section) =>
                section.title.toLowerCase().includes(trimmedQuery) ||
                section.excerpt.toLowerCase().includes(trimmedQuery) ||
                section.category.label.toLowerCase().includes(trimmedQuery)
        );
    }, [allSections, activeCategory, trimmedQuery]);

    const ActiveDocument = activeCategory ? DOCUMENT_COMPONENTS[activeCategory] : null;

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
        setActiveCategory(null);
        setExpandedId(null);
    };

    const handleCategoryClick = (categoryKey) => {
        setSearchQuery("");
        setActiveCategory((prev) => (prev === categoryKey ? null : categoryKey));
        setExpandedId(null);
        setMobileFiltersOpen(false);
    };

    const handleBackToOverview = () => {
        setSearchQuery("");
        setActiveCategory(null);
        setExpandedId(null);
    };

    const handleSectionClick = (section) => {
        setExpandedId((prev) => (prev === section.id ? null : section.id));
    };

    const quickLinkCards = CATEGORIES.filter((category) => QUICK_LINKS.includes(category.key));

    return (
        <div className="flex flex-col h-full w-full">
            <button
                type="button"
                onClick={() => navigate(-1)}
                className="md:hidden flex items-center gap-1.5 px-4 pt-4 text-[14px] font-semibold text-[#365975]"
                aria-label="Go back"
            >
                <IoArrowBack className="text-[20px]" />
                Back
            </button>

            <div className="flex flex-col md:flex-row flex-1 w-full overflow-hidden gap-6 px-4 md:px-8 py-6 md:py-20">
                {/* Search + category filter panel */}
                <aside className="md:w-64 md:shrink-0 rounded-2xl border border-[#D5E7F2] bg-white h-fit">
                    <button
                        type="button"
                        onClick={() => setMobileFiltersOpen((prev) => !prev)}
                        className="md:hidden flex w-full items-center justify-between px-5 py-4 text-[14px] font-semibold text-gray-900"
                    >
                        <span className="flex items-center gap-2">
                            <IoSearchOutline className="text-[16px] text-[#7A8A95]" />
                            Search &amp; Categories
                        </span>
                        <motion.span
                            animate={{ rotate: mobileFiltersOpen ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                            className="inline-flex"
                        >
                            <IoChevronDown className="text-[16px] text-[#587085]" />
                        </motion.span>
                    </button>

                    <AnimatePresence initial={false}>
                        {mobileFiltersOpen && (
                            <motion.div
                                key="mobile-filters"
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden md:hidden"
                            >
                                <FilterPanelContent
                                    searchQuery={searchQuery}
                                    onSearchChange={handleSearchChange}
                                    activeCategory={activeCategory}
                                    onCategoryClick={handleCategoryClick}
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <div className="hidden md:block">
                        <FilterPanelContent
                            searchQuery={searchQuery}
                            onSearchChange={handleSearchChange}
                            activeCategory={activeCategory}
                            onCategoryClick={handleCategoryClick}
                        />
                    </div>
                </aside>

                {/* Content */}
                <main className={`flex-1 min-w-0 ${ActiveDocument ? "overflow-hidden" : "overflow-y-auto"}`}>
                    {ActiveDocument ? (
                        <div className="flex flex-col h-full gap-3">
                            <button
                                type="button"
                                onClick={handleBackToOverview}
                                className="flex w-fit shrink-0 items-center gap-1.5 text-[13px] font-semibold text-[#365975] hover:text-[#2E9DDA]"
                            >
                                <IoArrowBack className="text-[15px]" />
                                Back to Safety Center 
                            </button>
                            <div className="min-h-0 flex-1 rounded-2xl border border-[#D5E7F2] overflow-hidden">
                                <ActiveDocument embedded />
                            </div>
                        </div>
                    ) : results ? (
                        <div className="flex flex-col gap-3">
                            <button
                                type="button"
                                onClick={handleBackToOverview}
                                className="flex w-fit items-center gap-1.5 text-[13px] font-semibold text-[#365975] hover:text-[#2E9DDA]"
                            >
                                <IoArrowBack className="text-[15px]" />
                                Back to Safety Center
                            </button>
                            {results.length === 0 ? (
                                <p className="text-[13px] text-gray-600">No results found. Try a different search term.</p>
                            ) : (
                                results.map((section) => {
                                    const isExpanded = expandedId === section.id;
                                    return (
                                        <div
                                            key={section.id}
                                            className="w-full rounded-xl border border-[#D5E7F2] overflow-hidden"
                                        >
                                            <button
                                                type="button"
                                                onClick={() => handleSectionClick(section)}
                                                className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left hover:bg-[#F8FBFE] transition-colors"
                                            >
                                                <span className="text-[14px] font-bold text-gray-900">{section.title}</span>
                                                <motion.span
                                                    animate={{ rotate: isExpanded ? 180 : 0 }}
                                                    transition={{ duration: 0.2 }}
                                                    className="inline-flex shrink-0"
                                                >
                                                    <IoChevronDown className="text-[16px] text-[#587085]" />
                                                </motion.span>
                                            </button>
                                            <AnimatePresence initial={false}>
                                                {isExpanded && (
                                                    <motion.div
                                                        key="content"
                                                        initial={{ height: 0, opacity: 0 }}
                                                        animate={{ height: "auto", opacity: 1 }}
                                                        exit={{ height: 0, opacity: 0 }}
                                                        transition={{ duration: 0.2 }}
                                                        className="overflow-hidden"
                                                    >
                                                        <p className="px-5 pb-4 text-[13px] text-gray-700 leading-relaxed whitespace-pre-line">
                                                            {renderWithEmailLink(section.answer || section.excerpt)}
                                                        </p>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    ) : (
                        <>
                            <h1 className="text-2xl font-bold text-gray-900">Safety Center</h1>
                            <p className="mt-2 text-[13px] text-gray-600">This safety policy applies to users and other individuals that are ages 13 and over. It covers how we protect our community, the tools available to you, and how to reach us if something goes wrong.</p>
                            <p className="text-[13px] text-gray-600">Effective: November 25th, 2025</p>

                            <h2 className="mt-6 text-[16px] font-bold text-gray-900">Our Commitment to Safety</h2>
                            <p className="mt-2 text-[13px] text-gray-700 leading-relaxed">
                                Welcome to the Trust and Safety Center. Here, we prioritize the security and well-being of our users. Our team is dedicated to ensuring a safe and trustworthy environment for all members of our community, combining automated protections with human review to catch harmful behavior early. Use the search box or the categories on the left to find our policies, tools, and support resources, or browse the shortcuts below to jump straight to the topics you care about most, from reporting a concern to understanding how enforcement decisions are made.
                            </p>

                            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {quickLinkCards.map((category) => (
                                    <button
                                        key={category.key}
                                        type="button"
                                        onClick={() => handleCategoryClick(category.key)}
                                        className="text-left rounded-xl border border-[#D5E7F2] px-5 py-4 hover:bg-[#F8FBFE] transition-colors"
                                    >
                                        <p className="flex items-center gap-2 text-[14px] font-bold text-gray-900">
                                            {category.label}
                                            <IoArrowForward className="text-[14px]" />
                                        </p>
                                        <p className="mt-1.5 text-[12px] text-gray-600 line-clamp-3 break-words">{category.sections[0].excerpt}</p>
                                    </button>
                                ))}
                            </div>

                        </>
                    )}
                </main>
            </div>
        </div>
    );
}
